
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetDashboardGraphsQuery } from "@/app/features/employee/performance/performance.api";
import { useAppFilters } from "@/hooks";
import { FILTER_KEYS } from "@/constants/keys.constants";
import useAuth from "@/hooks/useAuth";
import {
  UsersIcon,
  LaptopPerformanceIcon,
  TrendingUpIcon,
  StarIcon,
} from "@/icons";

function SummarySkeleton() {
  return (
    <div className="space-y-6 mt-6 animate-pulse">
      {/* Responsive Grid Container */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Bell Curve Chart Panel Skeleton */}
        <div className="lg:col-span-2 bg-theme-slate-800/60 border border-theme-gray-700/50 rounded-2xl p-6 h-[460px] flex flex-col justify-between">
          <div>
            <div className="h-8 w-48 bg-theme-gray-700/60 rounded-lg mb-2"></div>
            <div className="h-4 w-32 bg-theme-gray-700/40 rounded-lg"></div>
          </div>
          {/* Chart placeholder representation */}
          <div className="flex items-end justify-between h-[280px] w-full px-4 pt-10">
            <div className="w-[10%] h-[15%] bg-theme-gray-700/30 rounded-t-lg"></div>
            <div className="w-[12%] h-[30%] bg-theme-gray-700/35 rounded-t-lg"></div>
            <div className="w-[15%] h-[55%] bg-theme-gray-700/40 rounded-t-lg"></div>
            <div className="w-[18%] h-[85%] bg-theme-gray-700/50 rounded-t-lg"></div>
            <div className="w-[15%] h-[65%] bg-theme-gray-700/40 rounded-t-lg"></div>
            <div className="w-[12%] h-[40%] bg-theme-gray-700/35 rounded-t-lg"></div>
            <div className="w-[10%] h-[20%] bg-theme-gray-700/30 rounded-t-lg"></div>
          </div>
          <div className="flex justify-between px-2 pt-2 border-t border-theme-gray-700/20">
            <div className="h-3 w-16 bg-theme-gray-700/40 rounded"></div>
            <div className="h-3 w-16 bg-theme-gray-700/40 rounded"></div>
            <div className="h-3 w-16 bg-theme-gray-700/40 rounded"></div>
            <div className="h-3 w-16 bg-theme-gray-700/40 rounded"></div>
          </div>
        </div>

        {/* Stats Cards Breakdown Column Skeleton */}
        <div className="grid grid-cols-2 gap-4 h-full">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-theme-slate-800/60 border border-theme-gray-700/50 rounded-2xl p-5 flex flex-col items-center justify-center text-center h-[220px]">
              <div className="w-12 h-12 bg-theme-gray-700/50 rounded-xl mb-4"></div>
              <div className="h-3 w-24 bg-theme-gray-700/30 rounded mb-2"></div>
              <div className="h-6 w-12 bg-theme-gray-700/60 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Interpolate the scale factor smoothly between segment centers to eliminate vertical cliffs
const getInterpolatedScale = (x: number, distribution: any) => {
  const needsImprovement = distribution?.needsImprovement ?? 0;
  const average = distribution?.average ?? 0;
  const good = distribution?.good ?? 0;
  const outstanding = distribution?.outstanding ?? 0;

  // Segment center points
  const p1 = 12;
  const p2 = 37;
  const p3 = 62;
  const p4 = 87;

  if (x <= p1) return needsImprovement;
  if (x >= p4) return outstanding;

  if (x > p1 && x < p2) {
    const t = (x - p1) / (p2 - p1);
    const smoothT = t * t * (3 - 2 * t); // Smoothstep interpolation
    return needsImprovement + smoothT * (average - needsImprovement);
  }
  if (x >= p2 && x < p3) {
    const t = (x - p2) / (p3 - p2);
    const smoothT = t * t * (3 - 2 * t); // Smoothstep interpolation
    return average + smoothT * (good - average);
  }
  // x >= p3 && x < p4
  const t = (x - p3) / (p4 - p3);
  const smoothT = t * t * (3 - 2 * t); // Smoothstep interpolation
  return good + smoothT * (outstanding - good);
};

// Helper to generate high-resolution segmented bell curve data with micro-gaps (1% width)
// Peak heights are scaled relative to category counts from the distribution API response
const generateSegmentedBellCurve = (distribution: any) => {
  const data = [];
  const mean = 50; // center of the curve
  const stdDev = 20; // standard deviation for a nice shape

  const counts = [
    distribution?.needsImprovement ?? 0,
    distribution?.average ?? 0,
    distribution?.good ?? 0,
    distribution?.outstanding ?? 0,
  ];
  const maxCount = Math.max(...counts, 1); // Avoid division by zero

  for (let x = 0; x <= 100; x++) {
    // Normal distribution curve formula
    const exponent = -Math.pow(x - mean, 2) / (2 * Math.pow(stdDev, 2));
    const baseHeight = Math.exp(exponent);

    // Scale height smoothly across the curve
    const scale = getInterpolatedScale(x, distribution) / maxCount;
    const y = Math.round(2000 * baseHeight * scale);

    const category = x <= 23 ? 'Needs Improvement' : x <= 48 ? 'Average' : x <= 73 ? 'Good' : 'Outstanding';
    const count = x <= 23 ? (distribution?.needsImprovement ?? 0) : x <= 48 ? (distribution?.average ?? 0) : x <= 73 ? (distribution?.good ?? 0) : (distribution?.outstanding ?? 0);

    data.push({ y, score: x, category, count });
  }
  return data;
};

export default function Summary() {
  const { organizationId } = useAuth();
  const { page, limit, filters } = useAppFilters(
    FILTER_KEYS.PERFORMANCE_SUMMARY,
  );

  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetDashboardGraphsQuery({
    page,
    limit,
    organization_id: organizationId,
    ...filters,
  });

  const dashboardData = response?.data;

  const distribution = dashboardData?.distribution ?? {
    outstanding: 0,
    good: 0,
    average: 0,
    needsImprovement: 0,
  };

  const totalEmployees = dashboardData?.summary_metrics?.total_employees ?? 0;
  const avgScore = dashboardData?.summary_metrics?.avg_score ?? "0.00";
  const meanScore = dashboardData?.performance_stats?.mean ?? "0.00";
  const stdDev = dashboardData?.performance_stats?.stdDev ?? "0.00";

  const chartData = generateSegmentedBellCurve(distribution);

  if (isLoading || isFetching) {
    return <SummarySkeleton />;
  }

  return (
    <div className="space-y-6 mt-6">
      {/* Responsive Grid Container */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Bell Curve Chart Panel */}
        <div className="lg:col-span-2 bg-theme-slate-800 border border-theme-gray-700 rounded-2xl shadow-lg p-6 text-white">
          <h4 className="text-white text-3xl font-bold w-full mb-6">
            Performance Trend
          </h4>

          <div className="h-[360px] w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 10 }}
              >
                <defs>
                  {/* Fill Gradient (Horizontal with transparent gaps) */}
                  <linearGradient id="curveFillGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#854d0e" stopOpacity={0.6} />
                    <stop offset="23.5%" stopColor="#854d0e" stopOpacity={0.6} />
                    
                    <stop offset="23.5%" stopColor="transparent" stopOpacity={0} />
                    <stop offset="24.5%" stopColor="transparent" stopOpacity={0} />
                    
                    <stop offset="24.5%" stopColor="#059669" stopOpacity={0.65} />
                    <stop offset="48.5%" stopColor="#059669" stopOpacity={0.65} />
                    
                    <stop offset="48.5%" stopColor="transparent" stopOpacity={0} />
                    <stop offset="49.5%" stopColor="transparent" stopOpacity={0} />
                    
                    <stop offset="49.5%" stopColor="#047857" stopOpacity={0.65} />
                    <stop offset="73.5%" stopColor="#047857" stopOpacity={0.65} />
                    
                    <stop offset="73.5%" stopColor="transparent" stopOpacity={0} />
                    <stop offset="74.5%" stopColor="transparent" stopOpacity={0} />
                    
                    <stop offset="74.5%" stopColor="#0d9488" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#0d9488" stopOpacity={0.6} />
                  </linearGradient>

                  {/* Stroke Gradient (Horizontal with transparent gaps) */}
                  <linearGradient id="curveStrokeGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#854d0e" stopOpacity={1} />
                    <stop offset="23.5%" stopColor="#854d0e" stopOpacity={1} />
                    
                    <stop offset="23.5%" stopColor="transparent" stopOpacity={0} />
                    <stop offset="24.5%" stopColor="transparent" stopOpacity={0} />
                    
                    <stop offset="24.5%" stopColor="#059669" stopOpacity={1} />
                    <stop offset="48.5%" stopColor="#059669" stopOpacity={1} />
                    
                    <stop offset="48.5%" stopColor="transparent" stopOpacity={0} />
                    <stop offset="49.5%" stopColor="transparent" stopOpacity={0} />
                    
                    <stop offset="49.5%" stopColor="#047857" stopOpacity={1} />
                    <stop offset="73.5%" stopColor="#047857" stopOpacity={1} />
                    
                    <stop offset="73.5%" stopColor="transparent" stopOpacity={0} />
                    <stop offset="74.5%" stopColor="transparent" stopOpacity={0} />
                    
                    <stop offset="74.5%" stopColor="#0d9488" stopOpacity={1} />
                    <stop offset="100%" stopColor="#0d9488" stopOpacity={1} />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="score"
                  ticks={[12, 37, 62, 87]}
                  tickFormatter={(tick) => {
                    if (tick === 12)
                      return `Needs Improvement (${distribution.needsImprovement})`;
                    if (tick === 37)
                      return `Average (${distribution.average})`;
                    if (tick === 62) return `Good (${distribution.good})`;
                    if (tick === 87)
                      return `Outstanding (${distribution.outstanding})`;
                    return "";
                  }}
                  tick={{ fill: "#cbd5e1", fontSize: 11 }}
                  axisLine={{ stroke: "#475569" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#cbd5e1", fontSize: 11 }}
                  axisLine={{ stroke: "#475569" }}
                  tickLine={{ stroke: "#475569" }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #475569",
                    borderRadius: "12px",
                    color: "#fff",
                  }}
                  labelFormatter={(_, payload) => {
                    if (payload && payload.length > 0) {
                      return payload[0].payload.category;
                    }
                    return "";
                  }}
                  formatter={(_, __, props) => {
                    return [`${props.payload.count} Employee(s)`, "Count"];
                  }}
                />

                {/* Area Segment */}
                <Area
                  type="monotone"
                  dataKey="y"
                  stroke="url(#curveStrokeGradient)"
                  fill="url(#curveFillGradient)"
                  strokeWidth={2.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Stats Cards Breakdown Column */}
        <div className="grid grid-cols-2 gap-4">
          {/* Total Employees */}
          <div className="bg-theme-slate-800 border border-theme-gray-700 rounded-2xl shadow-lg p-5 flex flex-col items-center justify-center text-center">
            <div className="p-3.5 bg-indigo-500/10 text-indigo-400 rounded-xl mb-3">
              <UsersIcon size={26} />
            </div>
            <div className="text-theme-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
              Total Employees
            </div>
            <div className="text-white text-2xl font-bold">
              {totalEmployees}
            </div>
          </div>

          {/* Average Score */}
          <div className="bg-theme-slate-800 border border-theme-gray-700 rounded-2xl shadow-lg p-5 flex flex-col items-center justify-center text-center">
            <div className="p-3.5 bg-emerald-500/10 text-emerald-400 rounded-xl mb-3">
              <LaptopPerformanceIcon size={26} />
            </div>
            <div className="text-theme-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
              Average Score
            </div>
            <div className="text-white text-2xl font-bold">
              {avgScore}%
            </div>
          </div>

          {/* Mean Performance */}
          <div className="bg-theme-slate-800 border border-theme-gray-700 rounded-2xl shadow-lg p-5 flex flex-col items-center justify-center text-center">
            <div className="p-3.5 bg-purple-500/10 text-purple-400 rounded-xl mb-3">
              <TrendingUpIcon size={26} />
            </div>
            <div className="text-theme-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
              Mean Performance
            </div>
            <div className="text-white text-2xl font-bold">
              {meanScore}
            </div>
          </div>

          {/* Standard Deviation */}
          <div className="bg-theme-slate-800 border border-theme-gray-700 rounded-2xl shadow-lg p-5 flex flex-col items-center justify-center text-center">
            <div className="p-3.5 bg-amber-500/10 text-amber-400 rounded-xl mb-3">
              <StarIcon size={26} />
            </div>
            <div className="text-theme-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
              Standard Deviation
            </div>
            <div className="text-white text-2xl font-bold">
              {stdDev}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
